import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotes } from '@/hooks/useNotes';
import { getMemberInfo } from '@/hooks/useMembers';
import NoteCard from '@/components/ui/atoms/NoteCard';
import MLoader from '@/components/ui/atoms/MLoader';
import MHeader from '@/components/ui/organisms/MHeader';
import { formatDocumentDateShort } from '@/utils/date-utils';
import NoteDrawer from '@/pages/documents/notes/NoteDrawer';
import MDialog from '@/components/ui/atoms/MDialog';
import MEmptyCard from '@/components/ui/organisms/MEmptyCard';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { useSearchParams } from 'react-router-dom';
import MFooterNavigation from '@/components/ui/atoms/MFooterNavigation';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';
import styles from '@/components/ui/atoms/NoteCard.module.css';

const FamilyNotes: React.FC = () => {
  const navigate = useNavigate();
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const memberInfo = getMemberInfo();
  const loggedInMemberId = JSON.parse(localStorage.getItem('member') || '{}').id;
  const selectedMemberId = memberInfo.id || '';
  const { notes, fetchNotes, createNote, updateNote, deleteNote, loading } = useNotes(loggedInMemberId);

  // State for edit/delete
  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null);
  const [editingNoteData, setEditingNoteData] = React.useState<any>(null);
  const [deletingNoteId, setDeletingNoteId] = React.useState<string | null>(null);

  // Handler functions
  const handleEditClick = async (note: any) => {
    setEditingNoteId(note.id);
    // For now, we'll open the drawer with basic note data
    setEditingNoteData(note);
  };

  const handleDeleteClick = (note: any) => {
    setDeletingNoteId(note.id);
  };

  // Handler for add note button click (tracks analytics)
  const handleAddNoteClick = () => {
    // Track add notes click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_ADD_NOTES_CLICK,
    });
    
    setShowDrawer(true);
  };

  // Add note handler (family type forced)
  const handleNoteDrawerDone = async (fields: { title: string; content: string }) => {
    await createNote({
      memberId: selectedMemberId,
      title: fields.title,
      content: fields.content,
      type: 'family',
    });
    //toast.showToast({ message: 'Note added', variant: 'success' });
    await fetchNotes({ type: 'family' });
    setShowDrawer(false);
  };

  // Drawer done handler (edit or add)
  const handleDrawerDone = async (fields: { title: string; content: string }) => {
    if (editingNoteId) {
      await updateNote(editingNoteId, { ...fields, type: 'family' });
      //toast.showToast({ message: 'Note updated', variant: 'success' });
      setEditingNoteId(null);
      setEditingNoteData(null);
    } else {
      // Track add notes done (only for new notes, not edits)
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
        screenName: ANALYTICS_SCREEN_NAMES.FAMILY_ADD_NOTES_DONE,
      });
      
      await handleNoteDrawerDone(fields);
    }
    await fetchNotes({ type: 'family' });
    setShowDrawer(false);
  };

  // Close drawer handler
  const handleDrawerClose = () => {
    setEditingNoteId(null);
    setEditingNoteData(null);
    setShowDrawer(false);
    setSearchParams({});
  };

  // Delete note handlers
  const handleDeleteConfirm = async () => {
    if (deletingNoteId) {
      await deleteNote(deletingNoteId);
      //toast.showToast({ message: 'Note deleted', variant: 'success' });
      setDeletingNoteId(null);
      await fetchNotes({ type: 'family' });
    }
  };
  const handleDeleteCancel = () => setDeletingNoteId(null);

  // Open drawer if ?add=1 param is present
  React.useEffect(() => {
    if (searchParams.get('mode') === 'add') {
      setShowDrawer(true);
    }
  }, [searchParams]);

  // Fetch only family notes on mount
  React.useEffect(() => {
    fetchNotes({ type: 'family' });
  }, [fetchNotes]);

  return (
    <>
      <div style={{ padding: '0 24px' }}>
        <MHeader 
          title="Family notes" 
          module="Family" 
          memberInfo={memberInfo} 
          description='View and manage notes that have been  created for your family group.'
          onAdd={handleAddNoteClick}
          learnMoreContent='Family notes are visible to all members who have a Maatra account. Use them to capture health updates or important details so everyone stays informed and caregiving feels shared'
          drawerTitle='Learn More'
          showGradient
        />

        {/* Main content area with white background */}
        <div style={{ background: '#FFF', marginLeft: '-24px', marginRight: '-24px', padding: '0 24px', minHeight: 'calc(100vh - 200px)' }}>
          {loading ? (
            <MLoader />
          ) : (
            notes.length === 0 ? (
              <>
                <MSpacer mt={32} />
                <MEmptyCard
                  primaryTitle=''
                  secondaryTitle="Add a shared note to keep your family informed and organised. Family notes are visible to all family members with access."
                  primaryCTA={{
                    text: 'Add Family Note',
                    onClick: handleAddNoteClick,
                  }}
                />
              </>
            
            ) : (
              <div className="profile-flex-col-gap" style={{gap:0, marginTop: 0}}>
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className={styles.noteCardWrapper}
                  >
                    <NoteCard
                      key={note.id}
                      noteId={note.id}
                      title={note.title}
                      meta={`Created on ${formatDocumentDateShort(note.createdAt)}`}
                      avatar={note.member ? {
                        src: note.member.avatar || undefined,
                        text: `${note.member.firstName} ${note.member.lastName}`,
                        alt: `${note.member.firstName} ${note.member.lastName}`,
                      } : undefined}
                      onEdit={() => handleEditClick(note)}
                      onDelete={() => handleDeleteClick(note)}
                    />
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Add/Edit Note Drawer */}
      <NoteDrawer
          open={showDrawer || !!editingNoteId}
          onClose={handleDrawerClose}
          onDone={handleDrawerDone}
          loading={false}
          noteId={editingNoteId || undefined}
          initialData={editingNoteData}
          mode={editingNoteId ? 'edit' : 'add'}
          onDelete={editingNoteId ? () => setDeletingNoteId(editingNoteId) : undefined}
          noteType="family"
        />
      {/* Delete Confirmation Dialog */}
      <MDialog
        isOpen={!!deletingNoteId}
        onClose={handleDeleteCancel}
        title="Delete this note?"
        description="This note and all its comments will be permanently removed."
        primaryCtaText="Yes"
        onPrimaryCta={handleDeleteConfirm}
        secondaryCtaText="No"
        onSecondaryCta={handleDeleteCancel}
      />
      <MFooterNavigation />
    </>
  );
};

export default FamilyNotes;
